import { ReactNode } from 'react';

import { useAuthContext } from '../hooks';

interface Option {
  permissions: string[];
  requireAll?: boolean;
  component: ReactNode;
}

interface PermissionSwitchProps {
  options: Option[];
  fallback?: ReactNode;
}

/**
 * Used to render different components depending on the user's permissions
 * @param options - A list of options mapping permissions to a specified component
 * @param fallback - An optional fallback component
 * @returns The first matching component based on user permissions, or fallback
 */
export default function PermissionSwitch({ options, fallback = null }: PermissionSwitchProps) {
  const { permissions } = useAuthContext();

  for (let index = 0; index < options.length; index += 1) {
    const option = options[index];
    const hasPermission = option.requireAll
      ? option.permissions.every((permission) => permissions?.includes(permission))
      : option.permissions.some((permission) => permissions?.includes(permission));

    if (hasPermission) {
      return option.component;
    }
  }

  return fallback ?? null;
}
